import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { UserContext } from './UserContext';
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const AdminContext = createContext({
  isAdmin: false,
  isLoading: true,
  adminData: null
});

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminData, setAdminData] = useState(null);
  const [user, setUser] = useState(null);
  const { firestoreUser } = useContext(UserContext);
  // firestoreUser is a live snapshot of users/{uid}, so it changes on every
  // write to the admin's own user doc (last-active stamps, badge counts...).
  // Re-running the check on each one flipped isLoading on, AdminDashboard
  // swapped in its spinner, and the open tab, its filters and any modal were
  // thrown away mid-use (owner, 2026-10-21). Re-check only when something
  // the decision depends on changed, and show the spinner only the first
  // time for a given account.
  const lastCheckKeyRef = useRef(null);
  const checkedUidRef = useRef(null);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log('🛡️ Firebase Auth user changed:', firebaseUser?.uid);
      setUser(firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      console.log('🛡️ Admin check starting...');
      console.log('🛡️ Admin check - user:', user);
      console.log('🛡️ Admin check - firestoreUser:', firestoreUser);

      if (!user || !firestoreUser) {
        console.log('🛡️ Admin check - No user or firestoreUser, skipping');
        lastCheckKeyRef.current = null;
        checkedUidRef.current = null;
        setIsAdmin(false);
        setAdminData(null);
        setIsLoading(false);
        return;
      }

      const checkKey = [user.uid, firestoreUser.role, firestoreUser.username, firestoreUser.email].join('|');
      if (checkKey === lastCheckKeyRef.current) return;
      lastCheckKeyRef.current = checkKey;
      if (checkedUidRef.current !== user.uid) setIsLoading(true);

      try {
        // Check if user has admin role
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        console.log('🛡️ Admin check - User ID:', user.uid);
        console.log('🛡️ Admin check - User email:', user.email);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('🛡️ Admin check - User data:', userData);
          
          // Check for admin role or specific admin usernames
          const isUserAdmin = userData.role === 'admin' || 
                             userData.username === 'carabinerx' ||
                             userData.email === 'jkabanas@gmail.com';
          
          console.log('🛡️ Admin check - Is admin?', isUserAdmin);
          console.log('🛡️ Admin check - Username:', userData.username);
          console.log('🛡️ Admin check - Email:', userData.email);
          console.log('🛡️ Admin check - Role:', userData.role);
          
          setIsAdmin(isUserAdmin);
          
          if (isUserAdmin) {
            setAdminData({
              userId: user.uid,
              username: userData.username,
              email: userData.email,
              role: userData.role || 'admin',
              adminLevel: userData.adminLevel || 'full', // full, limited, read-only
              adminSince: userData.adminSince || userData.createdAt
            });
          } else {
            setAdminData(null);
          }
        } else {
          setIsAdmin(false);
          setAdminData(null);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setAdminData(null);
      }

      checkedUidRef.current = user.uid;
      setIsLoading(false);
    };

    checkAdminStatus();
  }, [user, firestoreUser]);

  const value = {
    isAdmin,
    isLoading,
    adminData
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
